export type NoteType = 'pin' | 'rect' | 'draw'

/** Whether the work happened. Deliberately separate from who is doing it. */
export type NoteStatus = 'open' | 'wont_fix' | 'done'

/** Who is doing the work. `ai` is the queue the MCP endpoint serves. */
export type NoteAssignee = 'none' | 'me' | 'ai'
export type WidgetCorner = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'
export type DesignerLayout = 'split' | 'list'
export type PlanTier = 'trial' | 'solo' | 'studio' | 'agency'
export type ProjectStatus = 'active' | 'snippet_missing' | 'paused' | 'archived'


export const TYPE_LABEL: Record<NoteType, string> = {
  pin: 'Note',
  rect: 'Highlight',
  draw: 'Sketch',
}

export const STATUS_LABEL: Record<NoteStatus, string> = {
  open: 'Open',
  wont_fix: "Won't fix",
  done: 'Done',
}

export const ASSIGNEE_LABEL: Record<NoteAssignee, string> = {
  none: 'Unassigned',
  me: 'I will fix',
  ai: 'AI will fix',
}

/**
 * Hard caps applied identically in the widget and at the ingest boundary.
 * These exist to bound abuse of unauthenticated write endpoints, not to be
 * friendly limits — the UI should never let a real user reach them.
 */
export const LIMITS = {
  maxNotes: 200,
  maxComment: 2000,
  maxPoints: 1000,
  maxSnapshotBytes: 8 * 1024 * 1024,
  maxRasterBytes: 8 * 1024 * 1024,
} as const

/** What `GET /api/w/config` hands back to the widget. */
export interface WidgetConfig {
  widget_corner: WidgetCorner
  /** Name of the feedback round the presented token belongs to. */
  round_name: string
  brand: {
    /** Studio name shown on the widget. */
    label: string | null
    /** Remove Spotnote marks entirely. */
    white_label: boolean
  }
}

/** Why a config request came back inactive. */
export type WidgetInactiveReason =
  | 'no_token'
  | 'unknown_token'
  | 'revoked'
  | 'round_ended'
  | 'paused'

/** The full config response: a live widget config, or a reason to stay asleep. */
export type WidgetConfigResponse =
  | ({ active: true } & WidgetConfig)
  | { active: false; reason: WidgetInactiveReason }
